import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { StickyNote } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export function RegisteredNotesPopover({ entityType, entityId, currentNotes }: { entityType: "lenders" | "brokers"; entityId: number; currentNotes: string | null | undefined }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentNotes ?? "");

  useEffect(() => {
    if (open) setValue(currentNotes ?? "");
  }, [open, currentNotes]);

  const mutation = useMutation({
    mutationFn: async (notes: string) =>
      apiRequest("PATCH", `/api/admin/referrals/${entityType}/${entityId}`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/referrals/${entityType}`] });
      toast({ title: "Notes saved" });
      setOpen(false);
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to save notes",
        description: parseErrorMessage(err, "Couldn't save these notes. Please try again."),
        variant: "destructive",
      }),
  });

  const trimmed = (currentNotes ?? "").trim();
  const singular = entityType === "lenders" ? "lender" : "broker";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-left text-xs text-gray-600 hover:text-gray-900 hover:underline flex items-start gap-1 max-w-[240px]"
          data-testid={`button-notes-${singular}-${entityId}`}
        >
          <StickyNote className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${trimmed ? "text-amber-600" : "text-gray-400"}`} />
          <span className="line-clamp-2 whitespace-pre-wrap" data-testid={`text-notes-${singular}-${entityId}`}>
            {trimmed ? trimmed : <span className="italic text-gray-400">Add note</span>}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="start">
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Admin notes</Label>
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Triage notes, follow-ups, contact context…"
            rows={6}
            className="text-sm"
            data-testid={`textarea-notes-${singular}-${entityId}`}
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`button-cancel-notes-${singular}-${entityId}`}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => mutation.mutate(value.trim())}
              disabled={mutation.isPending || value.trim() === (currentNotes ?? "").trim()}
              data-testid={`button-save-notes-${singular}-${entityId}`}
            >
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
